import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from './components/AdminLayout';
import { API_BASE_URL } from '../../config/apiConfig';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const PageTitle = styled.h1`
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  align-items: center;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const RefreshButton = styled.button`
  padding: 10px 20px;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #2980b9;
  }
  
  &:disabled {
    background-color: #bdc3c7;
    cursor: not-allowed;
  }
`;

const OverviewContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const OverviewCard = styled.div`
  background-color: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-left: 4px solid ${props => {
    if (props.type === 'success') return '#27ae60';
    if (props.type === 'warning') return '#f39c12';
    if (props.type === 'error') return '#e74c3c';
    return '#3498db';
  }};
`;

const CardTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #2c3e50;
  font-size: 16px;
`;

const CardValue = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: ${props => {
    if (props.type === 'success') return '#27ae60';
    if (props.type === 'warning') return '#f39c12';
    if (props.type === 'error') return '#e74c3c';
    return '#2c3e50';
  }};
  margin-bottom: 5px;
`;

const CardLabel = styled.div`
  font-size: 12px;
  color: #7f8c8d;
  text-transform: uppercase;
`;

const SectionContainer = styled.div`
  background-color: white;
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 20px 25px;
  border-bottom: 1px solid #ecf0f1;
  background-color: #f8f9fa;
`;

const SectionTitle = styled.h3`
  margin: 0;
  color: #2c3e50;
  font-size: 18px;
`;

const SectionContent = styled.div`
  padding: 25px;
`;

const AtRiskContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
`;

const AtRiskCard = styled.div`
  padding: 15px;
  border: 1px solid #ecf0f1;
  border-radius: 6px;
  background-color: #fff5f5;
  border-left: 4px solid #e74c3c;
`;

const PaymentId = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
`;

const PaymentDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  font-size: 12px;
  color: #7f8c8d;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
`;

const Th = styled.th`
  background-color: #f8f9fa;
  padding: 12px 15px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  color: #495057;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #dee2e6;
  color: #495057;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    if (props.status === 'failed') return '#e74c3c';
    if (props.status === 'timeout') return '#f39c12';
    return '#3498db';
  }};
  color: white;
`;

const RecommendationContainer = styled.div`
  display: grid;
  gap: 10px;
`;

const RecommendationCard = styled.div`
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid ${props => {
    if (props.severity === 'high') return '#e74c3c';
    if (props.severity === 'medium') return '#f39c12';
    if (props.severity === 'low') return '#3498db';
    return '#27ae60';
  }};
  background-color: ${props => {
    if (props.severity === 'high') return '#fdf2f2';
    if (props.severity === 'medium') return '#fefbf3';
    if (props.severity === 'low') return '#f0f8ff';
    return '#f0fff4';
  }};
`;

const RecommendationType = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 5px;
  text-transform: capitalize;
`;

const RecommendationMessage = styled.div`
  color: #555;
  font-size: 14px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 10px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  background-color: ${props => props.active ? '#3498db' : 'white'};
  color: ${props => props.active ? 'white' : '#495057'};
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background-color: ${props => props.active ? '#2980b9' : '#f8f9fa'};
  }
  
  &:disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
  }
`;

export default function TimeoutAnalysis() {
  const [analysisData, setAnalysisData] = useState(null);
  const [timedOutPayments, setTimedOutPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(7);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchAnalysisData = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('admin-token');
      
      const [analysisResponse, timeoutsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/payments/timeout-analysis?days=${selectedDays}`, {
          headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${API_BASE_URL}/admin/payments/timeouts?days=${selectedDays}&page=${currentPage}&limit=10`, {
          headers: {
            'Authorization': `${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!analysisResponse.ok || !timeoutsResponse.ok) {
        throw new Error('Failed to fetch timeout analysis data');
      }

      const [analysisData, timeoutsData] = await Promise.all([
        analysisResponse.json(),
        timeoutsResponse.json()
      ]);

      setAnalysisData(analysisData.data);
      setTimedOutPayments(timeoutsData.data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching timeout analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysisData();
  }, [selectedDays, currentPage]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatTimeoutDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const getTimeoutSeverity = (timeoutRate) => {
    const rate = parseFloat(timeoutRate);
    if (rate > 5) return 'error';
    if (rate > 2) return 'warning';
    return 'success';
  };

  if (loading && !analysisData) {
    return (
      <AdminLayout>
        <PageHeader>
          <PageTitle>Payment Timeout Analysis</PageTitle>
        </PageHeader>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <LoadingSpinner />
          Loading timeout analysis data...
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <PageHeader>
        <PageTitle>Payment Timeout Analysis</PageTitle>
        <FiltersContainer>
          <FilterSelect 
            value={selectedDays} 
            onChange={(e) => setSelectedDays(Number(e.target.value))}
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </FilterSelect>
          <RefreshButton onClick={fetchAnalysisData} disabled={loading}>
            {loading ? <LoadingSpinner /> : '🔄'} Refresh
          </RefreshButton>
        </FiltersContainer>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {analysisData && (
        <>
          <OverviewContainer>
            <OverviewCard type="info">
              <CardTitle>Total Payments</CardTitle>
              <CardValue>{analysisData.overview?.total_payments || 0}</CardValue>
              <CardLabel>In selected period</CardLabel>
            </OverviewCard>

            <OverviewCard type="success">
              <CardTitle>Success Rate</CardTitle>
              <CardValue type="success">
                {analysisData.overview?.success_rate || '0%'}
              </CardValue>
              <CardLabel>{analysisData.overview?.successful_payments || 0} successful</CardLabel>
            </OverviewCard>

            <OverviewCard type={getTimeoutSeverity(analysisData.overview?.timeout_rate)}>
              <CardTitle>Timeout Rate</CardTitle>
              <CardValue type={getTimeoutSeverity(analysisData.overview?.timeout_rate)}>
                {analysisData.overview?.timeout_rate || '0%'}
              </CardValue>
              <CardLabel>{analysisData.overview?.timed_out_payments || 0} timed out</CardLabel>
            </OverviewCard>

            <OverviewCard type={analysisData.at_risk_payments?.count > 10 ? 'warning' : 'info'}>
              <CardTitle>At Risk</CardTitle>
              <CardValue type={analysisData.at_risk_payments?.count > 10 ? 'warning' : 'info'}>
                {analysisData.at_risk_payments?.count || 0}
              </CardValue>
              <CardLabel>Payments near timeout</CardLabel>
            </OverviewCard>
          </OverviewContainer>

          {/* At Risk Payments */}
          <SectionContainer>
            <SectionHeader>
              <SectionTitle>⚠️ Payments At Risk (12-15 minutes old)</SectionTitle>
            </SectionHeader>
            <SectionContent>
              {analysisData.at_risk_payments?.count > 0 ? (
                <AtRiskContainer>
                  {analysisData.at_risk_payments.payments.map((payment) => (
                    <AtRiskCard key={payment.paymentId}>
                      <PaymentId>{payment.paymentId}</PaymentId>
                      <PaymentDetails>
                        <DetailItem>
                          <span>Order ID:</span>
                          <span>{payment.orderId}</span>
                        </DetailItem>
                        <DetailItem>
                          <span>Type:</span>
                          <span>{payment.paymentType}</span>
                        </DetailItem>
                        <DetailItem>
                          <span>Amount:</span>
                          <span>{formatCurrency(payment.amount)}</span>
                        </DetailItem>
                        <DetailItem>
                          <span>Age:</span>
                          <span>{payment.minutes_since_creation} minutes</span>
                        </DetailItem>
                      </PaymentDetails>
                    </AtRiskCard>
                  ))}
                </AtRiskContainer>
              ) : (
                <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '20px' }}>
                  No payments currently at risk of timing out
                </div>
              )}
            </SectionContent>
          </SectionContainer>

          {/* Recent Timed Out Payments */}
          <SectionContainer>
            <SectionHeader>
              <SectionTitle>💔 Recent Timed Out Payments</SectionTitle>
            </SectionHeader>
            <SectionContent>
              {timedOutPayments?.payments?.length > 0 ? (
                <>
                  <TableContainer>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Payment ID</Th>
                          <Th>Order ID</Th>
                          <Th>Type</Th>
                          <Th>Amount</Th>
                          <Th>Timeout Duration</Th>
                          <Th>Failed At</Th>
                          <Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {timedOutPayments.payments.map((payment) => (
                          <tr key={payment.paymentId}>
                            <Td>{payment.paymentId}</Td>
                            <Td>{payment.orderId}</Td>
                            <Td>{payment.paymentType}</Td>
                            <Td>{formatCurrency(payment.amount)}</Td>
                            <Td>
                              {formatTimeoutDuration(payment.timeout_info?.timeout_minutes || 0)}
                            </Td>
                            <Td>
                              {new Date(payment.processedAt).toLocaleString()}
                            </Td>
                            <Td>
                              <StatusBadge status={payment.timeout_info?.auto_failed ? 'timeout' : 'failed'}>
                                {payment.timeout_info?.auto_failed ? 'Auto Timeout' : 'Failed'}
                              </StatusBadge>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableContainer>

                  {timedOutPayments.pagination && (
                    <Pagination>
                      <PaginationButton
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={!timedOutPayments.pagination.has_prev}
                      >
                        Previous
                      </PaginationButton>
                      <PaginationButton active>
                        {timedOutPayments.pagination.current_page} of {timedOutPayments.pagination.total_pages}
                      </PaginationButton>
                      <PaginationButton
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={!timedOutPayments.pagination.has_next}
                      >
                        Next
                      </PaginationButton>
                    </Pagination>
                  )}
                </>
              ) : (
                <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '20px' }}>
                  No timed out payments found in the selected period
                </div>
              )}
            </SectionContent>
          </SectionContainer>

          {/* Recommendations */}
          <SectionContainer>
            <SectionHeader>
              <SectionTitle>💡 Recommendations</SectionTitle>
            </SectionHeader>
            <SectionContent>
              <RecommendationContainer>
                {analysisData.recommendations?.map((rec, index) => (
                  <RecommendationCard key={index} severity={rec.severity}>
                    <RecommendationType>{rec.type.replace(/_/g, ' ')}</RecommendationType>
                    <RecommendationMessage>{rec.message}</RecommendationMessage>
                  </RecommendationCard>
                ))}
              </RecommendationContainer>
            </SectionContent>
          </SectionContainer>

          {/* Statistics Summary */}
          {timedOutPayments?.statistics && (
            <SectionContainer>
              <SectionHeader>
                <SectionTitle>📊 Timeout Statistics</SectionTitle>
              </SectionHeader>
              <SectionContent>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <strong>Total Amount Lost:</strong><br/>
                    <span style={{ fontSize: '18px', color: '#e74c3c' }}>
                      {formatCurrency(timedOutPayments.statistics.total_amount_lost || 0)}
                    </span>
                  </div>
                  <div>
                    <strong>By Payment Type:</strong><br/>
                    {timedOutPayments.statistics.by_payment_type?.map((stat) => (
                      <div key={stat.paymentType} style={{ fontSize: '14px', marginTop: '5px' }}>
                        {stat.paymentType}: {stat.count} ({formatCurrency(stat.total_amount || 0)})
                      </div>
                    ))}
                  </div>
                </div>
              </SectionContent>
            </SectionContainer>
          )}
        </>
      )}
    </AdminLayout>
  );
} 